import {inject, Injectable, signal} from '@angular/core';
import {catchError, finalize, map, Observable, of, shareReplay, Subject, tap} from 'rxjs';

import type {FeedbackManagementRequest, ProductFeatureFeedbackPendingResult} from '@app/core/interfaces';
import type {SurveyPopoverRef} from '@shared/components/survey-popover';

import {ProductFeatureApiService} from './product-feature-api.service';

/** session اپ برای product-feature (enabled، pending، پاپ‌اور بازخورد) — {@link ProductFeatureSessionService.dismiss} */
@Injectable({providedIn: 'root'})
export class ProductFeatureSessionService {
  private readonly productFeatureApi = inject(ProductFeatureApiService);

  private activeRef: SurveyPopoverRef | null = null;

  private feedbackPendingSessionLoaded = false;
  private feedbackPendingInflight$: Observable<boolean> | null = null;

  private enabledSessionLoaded = false;
  private enabledInflight$: Observable<boolean> | null = null;

  private readonly feedbackManagementRequestSubject = new Subject<FeedbackManagementRequest>();

  /** کلیدهای فیچر فعال برای actor (از `GET .../enabled`). */
  readonly enabledFeatureKeys = signal<string[]>([]);

  /** آیتم‌های بازخورد در انتظار نمایش (از `GET .../feedback-pending`). */
  readonly feedbackPending = signal<ProductFeatureFeedbackPendingResult[]>([]);

  /** رویداد نمایش نظرسنجی — subscribe در `MainComponent`. */
  readonly feedbackManagementRequest$: Observable<FeedbackManagementRequest> =
    this.feedbackManagementRequestSubject.asObservable();

  setFeedbackManagementRequest(request: FeedbackManagementRequest): void {
    this.feedbackManagementRequestSubject.next(request);
  }

  registerOpen(ref: SurveyPopoverRef): void {
    this.activeRef = ref;
  }

  unregister(): void {
    this.activeRef = null;
  }

  dismiss(): void {
    this.activeRef?.close();
    this.activeRef = null;
  }

  isFeatureEnabled(key: string): boolean {
    return this.enabledFeatureKeys().includes(key);
  }

  /** یک‌بار در هر ورود به سامانه (پس از bootstrap) از سرور می‌خواند. */
  ensureEnabledLoaded(force = false): Observable<boolean> {
    if (!force && this.enabledSessionLoaded) {
      return of(true);
    }

    if (this.enabledInflight$) {
      return this.enabledInflight$;
    }

    this.enabledInflight$ = this.productFeatureApi.getEnabled().pipe(
      map(response => (response.success ? (response.result ?? []) : null)),
      tap(keys => {
        if (keys) {
          this.setEnabledFeatureKeys(keys);
        } else if (!this.enabledSessionLoaded) {
          this.setEnabledFeatureKeys([]);
        }
      }),
      map(keys => keys != null),
      catchError(() => {
        // Keep last good keys on refresh flake; only empty on first failure.
        if (!this.enabledSessionLoaded) {
          this.setEnabledFeatureKeys([]);
        }
        return of(this.enabledSessionLoaded);
      }),
      finalize(() => {
        this.enabledInflight$ = null;
      }),
      shareReplay(1),
    );

    return this.enabledInflight$;
  }

  ensureFeedbackPendingLoaded(force = false): Observable<boolean> {
    if (!force && this.feedbackPendingSessionLoaded) {
      return of(true);
    }

    if (this.feedbackPendingInflight$) {
      return this.feedbackPendingInflight$;
    }

    this.feedbackPendingInflight$ = this.productFeatureApi.getFeedbackPending().pipe(
      map(response => (response.success ? (response.result ?? []) : null)),
      tap(items => {
        if (items) {
          this.setFeedbackPending(items);
        } else if (!this.feedbackPendingSessionLoaded) {
          this.setFeedbackPending([]);
        }
      }),
      map(items => items != null),
      catchError(() => {
        if (!this.feedbackPendingSessionLoaded) {
          this.setFeedbackPending([]);
        }
        return of(this.feedbackPendingSessionLoaded);
      }),
      finalize(() => {
        this.feedbackPendingInflight$ = null;
      }),
      shareReplay(1),
    );

    return this.feedbackPendingInflight$;
  }

  setEnabledFeatureKeys(keys: string[]): void {
    this.enabledFeatureKeys.set(keys);
    this.enabledSessionLoaded = true;
  }

  setFeedbackPending(items: ProductFeatureFeedbackPendingResult[]): void {
    this.feedbackPending.set(items);
    this.feedbackPendingSessionLoaded = true;
  }

  clearEnabledFeatureKeys(): void {
    this.enabledFeatureKeys.set([]);
    this.enabledSessionLoaded = false;
    this.enabledInflight$ = null;
  }

  clearFeedbackPending(): void {
    this.feedbackPending.set([]);
    this.feedbackPendingSessionLoaded = false;
    this.feedbackPendingInflight$ = null;
  }

  /** پس از ثبت موفق step-1، کلید را از لیست pending محلی حذف می‌کند. */
  removePendingByKey(key: string): void {
    this.feedbackPending.update(items => items.filter(item => item.key !== key));
  }
}
